import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Plus, Truck, CheckCircle, Loader2, PowerOff,
  LayoutGrid, List, Pencil, Trash2, Search, Star,
  Shield, ShieldCheck, ShieldAlert, Zap, Phone, Mail, Hash, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT_TYPES = ['open', 'enclosed', 'flatbed'];
const KANBAN_LIMIT = 500;

export const BLANK_FORM = {
  company_name: '', contact_name: '', phone: '', email: '',
  mc_number: '', dot_number: '', customer_id: '',
  is_active: true, equipment_types: [], soft_straps_supported: false,
  regions_served: [], insurance_limit: 0,
};

// ── Carrier category helpers ──────────────────────────────────────────────────

function getCategory(carrier) {
  if (carrier.is_active === false) return 'inactive';
  if (carrier.status === 'active_verified') return 'verified';
  return 'pending';
}

const CATEGORY_CONFIG = {
  pending:  { label: 'Pending Verification', dot: 'bg-amber-500',   pill: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',   header: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'   },
  verified: { label: 'Active & Verified',    dot: 'bg-emerald-500', pill: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', header: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  inactive: { label: 'Inactive',             dot: 'bg-slate-400',   pill: 'bg-slate-500/10 text-slate-500',                           header: 'bg-slate-500/10 text-slate-500 border-slate-500/20'                           },
};

export function StatusPill({ carrier }) {
  const cfg = CATEGORY_CONFIG[getCategory(carrier)];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

export function RatingBar({ value = 50 }) {
  const color = value >= 75 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  );
}

// ── Shared form ───────────────────────────────────────────────────────────────

export function CarrierForm({ form, setForm }) {
  const toggle = (field, value) =>
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }));

  const field = (label, key, props = {}) => (
    <div>
      <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">{label}</Label>
      <Input
        value={form[key]}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
        className="bg-transparent border-border h-9 text-sm"
        {...props}
      />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        {field('Company Name', 'company_name', { required: true, placeholder: 'Acme Transport' })}
        {field('Contact Name', 'contact_name', { placeholder: 'John Smith' })}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {field('Phone', 'phone', { required: true, placeholder: '+1 555 000 0000' })}
        {field('Email', 'email', { required: true, type: 'email', placeholder: 'contact@acme.com' })}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {field('MC Number', 'mc_number', { placeholder: 'MC-123456' })}
        {field('DOT Number', 'dot_number', { placeholder: '1234567' })}
      </div>
      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">
          Central Dispatch Customer ID
        </Label>
        <Input
          value={form.customer_id}
          onChange={e => setForm(prev => ({ ...prev, customer_id: e.target.value }))}
          placeholder="5bd50adc-667e-4ae5-9192-5d4c95146d0f"
          className="bg-transparent border-border h-9 text-sm font-mono"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">Insurance Limit ($)</Label>
          <Input
            type="number"
            value={form.insurance_limit}
            onChange={e => setForm(prev => ({ ...prev, insurance_limit: parseInt(e.target.value) || 0 }))}
            className="bg-transparent border-border h-9 text-sm"
          />
        </div>
        <div className="flex flex-col justify-end">
          <div className="flex items-center gap-3 h-9">
            <Switch
              id="form_is_active"
              checked={form.is_active}
              onCheckedChange={v => setForm(prev => ({ ...prev, is_active: v }))}
            />
            <Label htmlFor="form_is_active" className="text-sm cursor-pointer">Active</Label>
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium text-muted-foreground mb-2 block">Equipment Types</Label>
        <div className="flex gap-4">
          {EQUIPMENT_TYPES.map(type => (
            <label key={type} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={form.equipment_types.includes(type)}
                onCheckedChange={() => toggle('equipment_types', type)}
              />
              <span className="text-sm capitalize">{type}</span>
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox
          checked={form.soft_straps_supported}
          onCheckedChange={v => setForm(prev => ({ ...prev, soft_straps_supported: !!v }))}
        />
        <span className="text-sm">Soft straps supported <span className="text-muted-foreground">(EV-safe)</span></span>
      </label>
    </div>
  );
}

// ── FMCSA mini badge ─────────────────────────────────────────────────────────

function FmcsaMini({ data }) {
  if (!data) return (
    <span title="No FMCSA check" className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold">
      <Shield className="h-2.5 w-2.5" /> FMCSA
    </span>
  );
  if (data.overall_pass === true) return (
    <span title="FMCSA Passed" className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold">
      <ShieldCheck className="h-2.5 w-2.5" /> FMCSA
    </span>
  );
  if (data.overall_pass === false) return (
    <span title="FMCSA Issues" className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 font-semibold">
      <ShieldAlert className="h-2.5 w-2.5" /> FMCSA
    </span>
  );
  return (
    <span title="FMCSA Unknown" className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold">
      <Shield className="h-2.5 w-2.5" /> FMCSA
    </span>
  );
}

// ── Kanban card ───────────────────────────────────────────────────────────────

function KanbanCard({ carrier, onEdit, onDelete, onVerify, onToggle, fmcsaData }) {
  const category = getCategory(carrier);
  return (
    <div className="bg-card border border-border rounded-xl p-4 hover:border-accent/20 hover:shadow-md transition-all duration-200">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <Link
            to={`/admin/carriers/${carrier.id}`}
            className="font-semibold text-sm text-foreground hover:text-accent truncate block"
          >
            {carrier.company_name}
          </Link>
          {carrier.contact_name && (
            <p className="text-xs text-muted-foreground mt-0.5">{carrier.contact_name}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <StatusPill carrier={carrier} />
          <FmcsaMini data={fmcsaData} />
        </div>
      </div>

      {/* Contact */}
      <div className="space-y-1 mb-3">
        {carrier.phone && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Phone className="h-3 w-3 shrink-0" />
            <span>{carrier.phone}</span>
          </div>
        )}
        {carrier.email && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Mail className="h-3 w-3 shrink-0" />
            <span className="truncate">{carrier.email}</span>
          </div>
        )}
      </div>

      {/* Equipment chips */}
      {carrier.equipment_types?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {carrier.equipment_types.map(t => (
            <span key={t} className="px-2 py-0.5 rounded-md bg-muted text-[11px] font-medium text-muted-foreground capitalize">
              {t}
            </span>
          ))}
          {carrier.soft_straps_supported && (
            <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <Zap className="h-2.5 w-2.5" /> EV-safe
            </span>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-2 py-3 border-y border-border mb-3 text-xs">
        {carrier.mc_number && (
          <div>
            <span className="text-muted-foreground">MC# </span>
            <span className="font-mono font-medium">{carrier.mc_number}</span>
          </div>
        )}
        {carrier.dot_number && (
          <div>
            <span className="text-muted-foreground">DOT# </span>
            <span className="font-mono font-medium">{carrier.dot_number}</span>
          </div>
        )}
        <div>
          <span className="text-muted-foreground">Insurance </span>
          <span className="font-medium">${(carrier.insurance_limit || 0).toLocaleString()}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Deliveries </span>
          <span className="font-medium">{carrier.total_deliveries || 0}</span>
        </div>
      </div>

      {/* Rating */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Rating</span>
          <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
        </div>
        <RatingBar value={carrier.rating_internal ?? 50} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {category === 'pending' && (
          <button
            onClick={() => onVerify(carrier.id)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold hover:bg-emerald-500/20 transition-colors"
          >
            <CheckCircle className="h-3.5 w-3.5" /> Verify
          </button>
        )}
        <button
          onClick={() => onToggle(carrier.id)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:text-foreground hover:bg-muted/80 transition-colors"
        >
          <PowerOff className="h-3.5 w-3.5" />
          {carrier.is_active !== false ? 'Deactivate' : 'Activate'}
        </button>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => onEdit(carrier)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(carrier)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── List view columns ─────────────────────────────────────────────────────────

const COLS = 'grid grid-cols-[1fr_120px_150px_100px_80px_90px_160px_100px] items-center gap-4 px-6';

// ── Pagination controls ───────────────────────────────────────────────────────

function Pagination({ page, total, limit, onPageChange }) {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-border">
      <p className="text-sm text-muted-foreground">
        Showing <span className="font-medium text-foreground">{start}–{end}</span> of{' '}
        <span className="font-medium text-foreground">{total}</span> carriers
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground text-sm">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                p === page
                  ? 'bg-accent text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CarriersPage() {
  const { token } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fmcsaMap, setFmcsaMap] = useState({});
  const searchTimer = useRef(null);
  const currentSearch = useRef('');
  const currentMode = useRef('list');
  const currentPageSize = useRef(20);

  // Dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [editCarrier, setEditCarrier] = useState(null);
  const [deleteCarrier, setDeleteCarrier] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);

  // ── API ────────────────────────────────────────────────────────────────────

  const fetchPage = useCallback(async (q, pg, mode) => {
    setLoading(true);
    const lim = mode === 'list' ? currentPageSize.current : KANBAN_LIMIT;
    try {
      const params = new URLSearchParams({ page: pg, limit: lim });
      if (q) params.set('search', q);
      const res = await fetch(`${API_URL}/api/admin/carriers?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCarriers(data.items);
        setTotal(data.total);
        const ids = data.items.map(c => c.id);
        if (ids.length) {
          fetch(`${API_URL}/api/admin/carriers/fmcsa-latest`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids }),
          }).then(r => r.ok ? r.json() : {}).then(setFmcsaMap).catch(() => {});
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPage('', 1, 'list');
  }, [fetchPage]);

  const handleSearch = (val) => {
    setSearch(val);
    currentSearch.current = val;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchPage(val, 1, currentMode.current);
    }, 400);
  };

  const handleViewMode = (mode) => {
    currentMode.current = mode;
    setViewMode(mode);
    setPage(1);
    fetchPage(currentSearch.current, 1, mode);
  };

  const handlePageChange = (pg) => {
    setPage(pg);
    fetchPage(currentSearch.current, pg, currentMode.current);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const refresh = () => fetchPage(currentSearch.current, page, currentMode.current);

  const handlePageSizeChange = (size) => {
    currentPageSize.current = size;
    setPageSize(size);
    setPage(1);
    fetchPage(currentSearch.current, 1, currentMode.current);
  };

  const handleCreate = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create');
      toast.success('Carrier created');
      setAddOpen(false);
      setForm(BLANK_FORM);
      refresh();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleUpdate = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${editCarrier.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update');
      toast.success('Carrier updated');
      setEditCarrier(null);
      setForm(BLANK_FORM);
      refresh();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${deleteCarrier.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete');
      toast.success('Carrier deleted');
      setDeleteCarrier(null);
      refresh();
    } catch (err) { toast.error(err.message); }
  };

  const handleVerify = async id => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${id}/verify`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to verify');
      toast.success('Carrier verified');
      refresh();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggle = async id => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${id}/toggle-active`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to toggle');
      const data = await res.json();
      toast.success(`Carrier ${data.is_active ? 'activated' : 'deactivated'}`);
      refresh();
    } catch (err) { toast.error(err.message); }
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const byCategory = {
    pending:  carriers.filter(c => getCategory(c) === 'pending'),
    verified: carriers.filter(c => getCategory(c) === 'verified'),
    inactive: carriers.filter(c => getCategory(c) === 'inactive'),
  };

  const openEdit = carrier => {
    setForm({
      company_name: carrier.company_name || '',
      contact_name: carrier.contact_name || '',
      phone: carrier.phone || '',
      email: carrier.email || '',
      mc_number: carrier.mc_number || '',
      dot_number: carrier.dot_number || '',
      customer_id: carrier.customer_id || '',
      is_active: carrier.is_active !== false,
      equipment_types: carrier.equipment_types || [],
      soft_straps_supported: carrier.soft_straps_supported || false,
      regions_served: carrier.regions_served || [],
      insurance_limit: carrier.insurance_limit || 0,
    });
    setEditCarrier(carrier);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="space-y-6" data-testid="admin-carriers">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-foreground">Carriers</h1>
            <p className="text-sm text-muted-foreground">
              {total} carrier{total !== 1 ? 's' : ''} in network
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, MC#, email…"
                value={search}
                onChange={e => handleSearch(e.target.value)}
                className="pl-9 w-56 h-9 bg-transparent border-border text-sm"
              />
            </div>

            {/* View toggle */}
            <div className="flex border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => handleViewMode('kanban')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'kanban' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <LayoutGrid className="h-4 w-4" /> Kanban
              </button>
              <button
                onClick={() => handleViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-l border-border transition-colors ${
                  viewMode === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <List className="h-4 w-4" /> List
              </button>
            </div>

            {/* Page size (list mode only) */}
            {viewMode === 'list' && (
              <select
                value={pageSize}
                onChange={e => handlePageSizeChange(Number(e.target.value))}
                className="h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground cursor-pointer"
              >
                {[20, 50, 100].map(n => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
            )}

            {/* Add carrier */}
            <Button
              onClick={() => { setForm(BLANK_FORM); setAddOpen(true); }}
              className="bg-accent text-white hover:bg-accent/90 h-9"
              data-testid="add-carrier-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Carrier
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
          </div>
        ) : viewMode === 'kanban' ? (

          /* ── Kanban ── */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(['pending', 'verified', 'inactive']).map(cat => {
              const cfg = CATEGORY_CONFIG[cat];
              const cards = byCategory[cat];
              return (
                <div key={cat}>
                  {/* Column header */}
                  <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl border mb-4 ${cfg.header}`}>
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
                      <span className="font-semibold text-sm">{cfg.label}</span>
                    </div>
                    <span className="text-xs font-bold opacity-70">{cards.length}</span>
                  </div>

                  <div className="space-y-3">
                    {cards.map(carrier => (
                      <KanbanCard
                        key={carrier.id}
                        carrier={carrier}
                        onEdit={openEdit}
                        onDelete={setDeleteCarrier}
                        onVerify={handleVerify}
                        onToggle={handleToggle}
                        fmcsaData={fmcsaMap[carrier.id]}
                      />
                    ))}
                    {cards.length === 0 && (
                      <div className="text-center py-10 text-muted-foreground/50 text-sm border border-dashed border-border rounded-xl">
                        No carriers
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

        ) : (

          /* ── List ── */
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            {/* Table header */}
            <div className={`${COLS} py-3 border-b border-border bg-muted/40`}>
              {['Company / Contact', 'Equipment', 'MC / DOT', 'Insurance', 'Rating', 'FMCSA', 'Status', 'Actions'].map((h, i) => (
                <span key={h} className={`text-[11px] font-semibold text-muted-foreground uppercase tracking-wider ${i === 3 || i === 4 ? 'text-right' : i === 7 ? 'text-right' : i === 5 ? 'text-center' : ''}`}>
                  {h}
                </span>
              ))}
            </div>

            {carriers.length === 0 ? (
              <div className="py-20 text-center text-muted-foreground">
                <Truck className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>{search ? 'No carriers match your search' : 'No carriers yet'}</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {carriers.map(carrier => (
                  <div key={carrier.id} className={`${COLS} py-4 hover:bg-muted/40 transition-colors`}>

                    {/* Company / contact */}
                    <div className="min-w-0">
                      <Link
                        to={`/admin/carriers/${carrier.id}`}
                        className="font-semibold text-sm text-foreground hover:text-accent truncate block"
                      >
                        {carrier.company_name}
                      </Link>
                      {carrier.contact_name && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{carrier.contact_name}</p>
                      )}
                      {carrier.mc_number && (
                        <p className="text-[11px] text-muted-foreground/60 font-mono mt-0.5">MC# {carrier.mc_number}</p>
                      )}
                    </div>

                    {/* Equipment */}
                    <div className="flex flex-wrap gap-1">
                      {carrier.equipment_types?.length > 0
                        ? carrier.equipment_types.map(t => (
                          <span key={t} className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-medium text-muted-foreground capitalize">{t}</span>
                        ))
                        : <span className="text-xs text-muted-foreground">—</span>
                      }
                    </div>

                    {/* MC / DOT */}
                    <div className="text-xs space-y-0.5">
                      {carrier.mc_number && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Hash className="h-3 w-3" /><span className="font-mono">{carrier.mc_number}</span>
                        </div>
                      )}
                      {carrier.dot_number && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Shield className="h-3 w-3" /><span className="font-mono">{carrier.dot_number}</span>
                        </div>
                      )}
                      {!carrier.mc_number && !carrier.dot_number && <span className="text-muted-foreground">—</span>}
                    </div>

                    {/* Insurance */}
                    <div className="text-right">
                      <span className="text-sm font-semibold text-foreground tabular-nums">
                        ${(carrier.insurance_limit || 0).toLocaleString()}
                      </span>
                    </div>

                    {/* Rating */}
                    <div className="text-right">
                      <span className="text-sm font-bold text-foreground tabular-nums">
                        {carrier.rating_internal ?? 50}
                      </span>
                      <span className="text-xs text-muted-foreground">/100</span>
                    </div>

                    {/* FMCSA */}
                    <div className="flex justify-center">
                      {(() => {
                        const f = fmcsaMap[carrier.id];
                        if (!f) return <Shield className="h-4 w-4 text-muted-foreground/30" title="No FMCSA check" />;
                        if (f.overall_pass === true) return <ShieldCheck className="h-4 w-4 text-emerald-500" title="FMCSA Passed" />;
                        if (f.overall_pass === false) {
                          const flags = [
                            f.flag_no_authority && 'No Authority',
                            f.flag_safety_risk  && `Safety: ${f.safety_rating}`,
                            f.flag_no_insurance && 'No Insurance',
                            f.flag_oos          && 'Out of Service',
                          ].filter(Boolean).join(' · ');
                          return <ShieldAlert className="h-4 w-4 text-red-500" title={`FMCSA Issues: ${flags}`} />;
                        }
                        return <Shield className="h-4 w-4 text-muted-foreground" title="FMCSA Unknown" />;
                      })()}
                    </div>

                    {/* Status */}
                    <div className="flex justify-center">
                      <StatusPill carrier={carrier} />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-1">
                      {getCategory(carrier) === 'pending' && (
                        <button
                          onClick={() => handleVerify(carrier.id)}
                          className="p-1.5 rounded-lg text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                          title="Verify carrier"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleToggle(carrier.id)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title={carrier.is_active !== false ? 'Deactivate' : 'Activate'}
                      >
                        <PowerOff className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEdit(carrier)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteCarrier(carrier)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            <Pagination page={page} total={total} limit={pageSize} onPageChange={handlePageChange} />
          </div>
        )}
      </div>

      {/* ── Add Dialog ── */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg">Add New Carrier</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5 pt-2" data-testid="add-carrier-form">
            <CarrierForm form={form} setForm={setForm} />
            <Button type="submit" className="w-full bg-accent text-white hover:bg-accent/90" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Carrier'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog open={!!editCarrier} onOpenChange={open => { if (!open) setEditCarrier(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg">Edit Carrier</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-5 pt-2">
            <CarrierForm form={form} setForm={setForm} />
            <Button type="submit" className="w-full bg-accent text-white hover:bg-accent/90" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={!!deleteCarrier} onOpenChange={open => { if (!open) setDeleteCarrier(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg">Delete Carrier</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteCarrier?.company_name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteCarrier(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-500 text-white hover:bg-red-600"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
}
